/**
 * Pure helpers for the upstream-to-downstream box plots.
 *
 * Kept free of the Nivo import so they can be unit tested: @nivo/core pulls in
 * d3 packages that ship ES modules, which the project's jest setup does not
 * transform. The line chart also reads the palette from here rather than from
 * the chart component, for the same reason.
 */

/* One hue per reach, dark for the selected year and light for the comparison,
   so two reaches drawn one above the other are never the same color. */
export const REACH_COLORS = {
  105: ["#1b5e20", "#a5d6a7"], // Cherry Creek
  106: ["#0d47a1", "#90caf9"], // Cottonwood Creek
  109: ["#4a148c", "#ce93d8"], // McMurdo Gulch
  112: ["#e65100", "#ffcc80"], // Shop Creek
  126: ["#006064", "#80deea"], // Piney Creek
};

export const FALLBACK_COLORS = ["#37474f", "#b0bec5"];

export const BENCHMARK_COLOR = "#c62828";

export const fmt = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? "--" : Number(v).toFixed(digits);

/**
 * Benchmark lines for the parameter, as Nivo cartesian markers: a dashed line
 * and nothing else. A parameter where low values are the concern gets a line
 * below the data, a two sided one such as pH gets both.
 * @param {object} parameter the parameter block from the API
 * @returns {array} marker configs, empty when there is no benchmark
 */
export const buildMarkers = (parameter) => {
  if (!parameter?.hasBenchmark) return [];

  /* No text on the line: the legend above the chart names the benchmark and
     gives its value, so a label here would say the same thing twice. */
  const line = (value) => ({
    axis: "y",
    value,
    lineStyle: {
      stroke: BENCHMARK_COLOR,
      strokeWidth: 2,
      strokeDasharray: "6 4",
    },
  });

  const markers = [];
  if (parameter.benchmarkHigh != null)
    markers.push(line(parameter.benchmarkHigh));
  if (parameter.benchmarkLow != null)
    markers.push(line(parameter.benchmarkLow));
  return markers;
};

/**
 * Describes the reach in words for a screen reader: the parameter, the period
 * and whether the median rises or falls between the first and last site.
 * @param {object} reach a reshaped reach
 * @param {object} parameter the parameter block from the API
 * @param {string} selectedLabel the label for the selected water year
 * @returns {string} the aria-label for the chart
 */
export const buildAriaLabel = (reach, parameter, selectedLabel) => {
  const boxes = (reach.boxes || []).filter(
    (b) => !b.isComparison && b.median != null
  );
  const unit =
    parameter?.unitName && parameter.unitName !== "None"
      ? ` ${parameter.unitName}`
      : "";
  const head =
    `${parameter?.paramDescrip ?? "Parameter"} along ${reach.reachLabel}, ` +
    `${selectedLabel}, ${reach.groups.length} sites from upstream to downstream`;

  if (boxes.length < 2) return `${head}.`;

  const first = boxes[0];
  const last = boxes[boxes.length - 1];
  const direction =
    last.median > first.median
      ? "increases"
      : last.median < first.median
      ? "decreases"
      : "is unchanged";

  return (
    `${head}. The median ${direction} from ${fmt(first.median)}${unit} at ` +
    `${first.locationName} to ${fmt(last.median)}${unit} at ${
      last.locationName
    }.`
  );
};

/**
 * The span the chart draws for a set of boxes: the lowest 5th percentile to
 * the highest 95th, the same quantiles handed to Nivo as whiskers.
 * @param {array} boxes boxes from the API, each with p05 and p95
 * @returns {{min: number, max: number}|null} null when nothing is drawable
 */
export const boxesDomain = (boxes) => {
  const d = (boxes ?? []).reduce(
    (acc, box) => ({
      min: Math.min(acc.min, box.p05 ?? Infinity),
      max: Math.max(acc.max, box.p95 ?? -Infinity),
    }),
    { min: Infinity, max: -Infinity }
  );
  return Number.isFinite(d.min) && Number.isFinite(d.max) ? d : null;
};

/**
 * The y axis for one chart.
 *
 * `base` is the span of what the chart draws, the 5th to 95th percentiles
 * across the boxes in scope. The benchmark line is pulled into the axis when
 * it sits within one and a half spans of the data, so a line just above the
 * boxes stays visible; a benchmark ten times higher than anything measured is
 * left out rather than crushing the boxes into a sliver, and reported back so
 * the legend can say so. Pass `parameter` as null when the benchmark has been
 * hidden from the legend and the axis should settle back onto the data.
 *
 * @param {object|null} base {min, max} of the drawn quantiles
 * @param {object|null} parameter the parameter block from the API, or null
 * @param {number} pad fraction of the span to leave clear above and below
 * @returns {{min: number|string, max: number|string, hiddenBenchmarks: array}}
 */
export const computeYDomain = (base, parameter, pad = 0.06) => {
  if (!base || !Number.isFinite(base.min) || !Number.isFinite(base.max)) {
    return { min: "auto", max: "auto", hiddenBenchmarks: [] };
  }

  let { min, max } = base;
  /* A single value, or every value identical, has no span to pad. */
  if (max === min) max = min + (Math.abs(min) || 1);
  const span = max - min;

  const hiddenBenchmarks = [];
  const consider = (value, label) => {
    if (value == null || !Number.isFinite(value)) return;
    if (value >= min && value <= max) return;
    if (value > max && value <= max + span * 1.5) {
      max = value;
    } else if (value < min && value >= min - span * 1.5) {
      min = value;
    } else {
      hiddenBenchmarks.push({ value, label });
    }
  };

  const twoSided = parameter?.isTwoSided;
  consider(
    parameter?.benchmarkHigh,
    twoSided ? "Benchmark (upper)" : "Benchmark"
  );
  consider(
    parameter?.benchmarkLow,
    twoSided ? "Benchmark (lower)" : "Benchmark"
  );

  const padding = (max - min) * pad;
  const lo = min - padding;
  return {
    /* A non-negative quantity starts at zero when zero is close, within a
       quarter of the top of the axis; pH or a dissolved oxygen band sitting
       well above zero keeps its own floor instead of being dragged down. */
    min: min >= 0 && lo < max * 0.25 ? 0 : lo,
    max: max + padding,
    hiddenBenchmarks,
  };
};
